import React, {useEffect, useMemo, useState} from "react";
import { Views } from 'react-big-calendar';
import PropTypes from "prop-types";
import BigCalendar from "react-big-calendar";
import EventCustomWeek from "./EventCustomWeek";
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from "moment";
import {TT_DELETE, TT_EDIT} from "../i18n/toolTips";
import {GenericTableButton} from "../buttons/buttonsActions";

const defaultEventColors = () => {
    return { style: { backgroundColor: "#000" } };
};

const EventCustomCalendar = ({ events, maxDaysBySlot= 3, onSelectEvent, onSelectSlot, minHour, maxHour, defaultDate, eventColors = defaultEventColors, onDeleteItineraryItem=()=>{}, eventDates=[] }) => {
    const { views } = useMemo(
        () => ({
            views: {
                week: EventCustomWeek,
                agenda: true
            },
        }),
        []
    );
    const localizer = BigCalendar.momentLocalizer(moment);
    const [maxEventsPerDay, setMaxEventsPerDay] = useState(1);
    const [activities, setActivities] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState(events);

    useEffect(()=>{
        localStorage.setItem("MAX-DAYS-CALENDAR", maxDaysBySlot);
    }, [maxDaysBySlot]);

    useEffect(() => {
        const uniqueTitles = events
            .map(item => item.title)
            .filter(title => title)
            .filter((value, index, self) => self.indexOf(value) === index);

        const formattedActivities = uniqueTitles.map(title => ({
            label: title,
            value: title
        }));

        setActivities(formattedActivities);
    }, [events]);

    useEffect(() => {
        const getDateString = (date) => date.toISOString().split('T')[0];
        const getHourString = (date) => date.getUTCHours().toString().padStart(2, '0') + ":00";
        const getMaximumPeriodOfTimes = () => {
            const eventCounts = {};
            filteredEvents.forEach(event => {
                const startDate = new Date(event.start);
                const dateString = getDateString(startDate);
                const startHour = getHourString(startDate);
                const key = `${dateString} ${startHour}`;
                if (!eventCounts[key]) {
                    eventCounts[key] = 0;
                }
                eventCounts[key]++;
            });
            return Object.entries(eventCounts).map(([key, count]) => ({
                period: key,
                count
            }));
        };

        if(filteredEvents.length){
            const orderedCounter = getMaximumPeriodOfTimes().sort((a, b) => b.count - a.count);
            setMaxEventsPerDay(orderedCounter[0].count);
        }
    }, [filteredEvents]);

    const calculateSlotHeight = () => {
        if(maxEventsPerDay>1){
            let mod = Math.trunc(maxEventsPerDay/4);
            if(mod>5){
                mod = 5;
            }
            return `rbc-time-slot${mod}x`;
        }
        return `rbc-time-slot1x`;
    }

    const scrollToTime = useMemo(() => {
        if (!filteredEvents.length) {
            const fallback = new Date(defaultDate || new Date());
            fallback.setHours(minHour.hour, minHour.min, 0, 0);
            return fallback;
        }

        const referenceDate = new Date(defaultDate || new Date());
        referenceDate.setHours(0, 0, 0, 0);

        const sameDayEvents = filteredEvents.filter(event => {
            const eventDate = new Date(event.start);
            eventDate.setHours(0, 0, 0, 0);
            return eventDate.getTime() === referenceDate.getTime();
        });

        const eventsToCheck = sameDayEvents.length ? sameDayEvents : filteredEvents;
        const earliestStart = eventsToCheck.reduce((earliest, event) => {
            const start = new Date(event.start);
            return start < earliest ? start : earliest;
        }, new Date(eventsToCheck[0].start));

        const scrollDate = new Date(defaultDate || earliestStart);
        scrollDate.setHours(earliestStart.getHours(), earliestStart.getMinutes(), 0, 0);
        return scrollDate;
    }, [filteredEvents, defaultDate, minHour]);

    const onApplyFilters = (activity, date, start, end) => {
        const filtered = events.filter(item => {
            let isValid = true;
            if (activity && item.title !== activity) {
                isValid = false;
            }

            if (date && new Date(item.start).toLocaleDateString() !== new Date(date).toLocaleDateString()) {
                isValid = false;
            }

            const itemStartDate = new Date(item.start);
            const itemEndDate = new Date(item.end);

            if (start) {
                const [startHours, startMinutes] = start.split(":").map(Number);
                const startDateTime = new Date(itemStartDate);
                startDateTime.setHours(startHours, startMinutes, 0, 0);
                if (startDateTime > itemStartDate) {
                    isValid = false;
                }
            }

            if (end) {
                const [endHours, endMinutes] = end.split(":").map(Number);
                const endDateTime = new Date(itemEndDate);
                endDateTime.setHours(endHours, endMinutes, 0, 0);

                if (endDateTime < itemEndDate) {
                    isValid = false;
                }
            }

            return isValid;
        });
        setFilteredEvents(filtered)
    };

    const CustomAgendaEvent = (itinerary) => <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>{itinerary?.event?.title}</span>
                <div className="float-right">
                    <GenericTableButton
                        toolTip={TT_EDIT}
                        icon={'fa-pen'}
                        permission={'edit'}
                        onClick={()=>onSelectEvent(itinerary?.event)}
                    />
                    <GenericTableButton
                        toolTip={TT_DELETE}
                        icon={'fa-trash'}
                        permission={'delete'}
                        onClick={() => onDeleteItineraryItem(itinerary?.event)}
                    />
                </div>
            </div>

    if(localizer){
        return <div>
            {/*<EventCustomCalendarFilter activities={activities} onFilterAction={onApplyFilters} />*/}
            <BigCalendar
                localizer={localizer}
                events={filteredEvents}
                selectable
                startAccessor="start"
                endAccessor="end"
                style={{ height: '75vh' }}
                onSelectEvent={onSelectEvent}
                onSelectSlot={onSelectSlot}
                eventDates={eventDates}
                eventPropGetter={eventColors}
                defaultDate={defaultDate}
                scrollToTime={scrollToTime}
                minHour={minHour}
                maxHour={maxHour}
                views={views}
                defaultView={Views.WEEK}
                className={calculateSlotHeight()}
                components={{
                    agenda: {
                        event: CustomAgendaEvent,
                    },
                }}
                messages={{
                    today: "Hoy",
                    previous: "Anterior",
                    next: "Siguiente",
                    day: "Día",
                    month: "Mes",
                    week: "Semana",
                    agenda: "Agenda",
                    showMore: function showMore(total) {
                        return `+${total} más`;
                    },
                }}
            />
        </div>
    }
};

EventCustomCalendar.propTypes = {
    events: PropTypes.array.isRequired,
    onSelectEvent: PropTypes.func.isRequired,
    onSelectSlot: PropTypes.func.isRequired,
    maxDaysBySlot: PropTypes.number,
    maxHour: PropTypes.shape({
        hour: PropTypes.number.isRequired,
        min: PropTypes.number.isRequired,
    }).isRequired,
    minHour: PropTypes.shape({
        hour: PropTypes.number.isRequired,
        min: PropTypes.number.isRequired,
    }).isRequired,
    eventColors: PropTypes.func,
    eventDates: PropTypes.array,
};

export default EventCustomCalendar;